import {
  Alert,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import type { UseFormReturnType } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { z } from 'zod';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { translating } from '../i18n/resolver.js';
import { useT } from '../i18n/useT.js';
import type { MessageKey } from '../i18n/da.js';
import { useContacts, useCreateContact, useUpdateContact } from '../api/hooks.js';
import type { Contact, UpdateContact } from '../api/hooks.js';

/**
 * Kontakter — customers and suppliers.
 *
 * A contact describes who someone **is now**, not a document they hold, so
 * unlike an invoice it stays editable. The edit that matters is the address:
 * `address`, `city` and `zip` are exactly what an electronic invoice is refused
 * without, so a contact missing them can be invoiced on paper and not
 * electronically. The list says which contacts are in that state rather than
 * leaving it to be discovered at the moment of sending.
 *
 * **There is no delete.** Issued invoices reference the contact, and an invoice
 * naming a customer who no longer exists is not a record of anything.
 * Deactivating takes it out of the pickers and keeps everything.
 */

const TYPE_LABELS: Record<Contact['type'], MessageKey> = {
  customer: 'contacts.type.customer',
  supplier: 'contacts.type.supplier',
  both: 'contacts.type.both',
};

/** Whether this contact could be sent an electronic invoice today. */
export function canBeEInvoiced(contact: Contact): boolean {
  return (
    contact.address !== null &&
    contact.address !== '' &&
    contact.city !== null &&
    contact.city !== '' &&
    contact.zip !== null &&
    contact.zip !== ''
  );
}

export function ContactsPage(): ReactNode {
  const t = useT();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [editing, setEditing] = useState<Contact | null>(null);
  const [createOpened, createModal] = useDisclosure(false);
  const [editOpened, editModal] = useDisclosure(false);

  const contacts = useContacts({ includeInactive });

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="center" wrap="wrap">
        <Title order={2}>{t('contacts.title')}</Title>
        <Button onClick={createModal.open}>{t('contacts.new')}</Button>
      </Group>

      <Group>
        <Switch
          label={t('contacts.showDisabled')}
          checked={includeInactive}
          onChange={(event) => {
            setIncludeInactive(event.currentTarget.checked);
          }}
        />
      </Group>

      {contacts.isPending && (
        <Center p="xl">
          <Loader />
        </Center>
      )}

      {contacts.isError && <ErrorMessage error={contacts.error} />}

      {contacts.isSuccess && contacts.data.items.length === 0 && (
        <Text c="dimmed">{t('contacts.none')}</Text>
      )}

      {contacts.isSuccess && contacts.data.items.length > 0 && (
        <Table.ScrollContainer minWidth={780}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('contacts.col.name')}</Table.Th>
                <Table.Th style={{ width: 120 }}>{t('contacts.col.type')}</Table.Th>
                <Table.Th style={{ width: 120 }}>{t('contacts.cvr')}</Table.Th>
                <Table.Th>{t('contacts.col.address')}</Table.Th>
                <Table.Th style={{ width: 110 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {contacts.data.items.map((contact) => (
                <Table.Tr key={contact.id} opacity={contact.isActive ? 1 : 0.55}>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <Text>{contact.name}</Text>
                      {!contact.isActive && (
                        <Badge size="xs" color="gray" variant="light">
                          {t('contacts.disabled')}
                        </Badge>
                      )}
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{t(TYPE_LABELS[contact.type])}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" ff="monospace">
                      {contact.cvr ?? ''}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {canBeEInvoiced(contact) ? (
                      <Text size="sm">
                        {contact.address}, {contact.zip} {contact.city}
                      </Text>
                    ) : (
                      /*
                        Said here rather than at the moment of sending: without
                        these three fields the API refuses the e-invoice with
                        einvoice_party_incomplete, and finding that out while
                        trying to send is finding it out too late.
                      */
                      <Badge color="yellow" variant="light" size="sm">
                        {t('contacts.missingAddress')}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Button
                      size="compact-sm"
                      variant="subtle"
                      onClick={() => {
                        setEditing(contact);
                        editModal.open();
                      }}
                    >
                      {t('contacts.edit')}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}

      <CreateContactModal opened={createOpened} onClose={createModal.close} />
      <EditContactModal
        contact={editing}
        opened={editOpened}
        onClose={() => {
          editModal.close();
          setEditing(null);
        }}
      />
    </Stack>
  );
}

// -----------------------------------------------------------------------------
// Shared field rules
// -----------------------------------------------------------------------------

/**
 * The optional identifiers, validated exactly as the database does.
 *
 * Empty means "not recorded" and is allowed; anything present has to be the
 * right shape. Letting a seven-digit CVR through would trade a message written
 * here for a 422 written elsewhere.
 */
const cvrField = z
  .string()
  .refine((value) => value === '' || /^\d{8}$/u.test(value), 'contacts.cvrInvalid');

const eanField = z
  .string()
  .refine((value) => value === '' || /^\d{13}$/u.test(value), 'contacts.eanInvalid');

const emailField = z
  .string()
  .refine(
    (value) => value === '' || z.email().safeParse(value).success,
    'contacts.emailInvalid',
  );

/**
 * One schema for both forms.
 *
 * Creating and editing collect the same fields, and giving them one values type
 * means the shared field group below is typed against a single form rather than
 * cast between two near-identical ones. `isActive` is part of it because the
 * edit form needs it; the create form simply does not render it, and does not
 * send it — a new contact is active, which is the API's own default.
 */
/*
  The schema carries message *keys*, not messages, and `contactMessage` turns
  one into text at render. The fields above are module-level constants shared by
  both modals, so threading `t` through them would mean rebuilding all three on
  every keystroke; a key is a stable string that translates late instead.
*/
const contactSchema = z.object({
  name: z.string().min(1, 'contacts.nameRequired'),
  type: z.enum(['customer', 'supplier', 'both']),
  cvr: cvrField,
  ean: eanField,
  email: emailField,
  address: z.string(),
  city: z.string(),
  zip: z.string(),
  isActive: z.boolean(),
});

type ContactValues = z.infer<typeof contactSchema>;

const EMPTY_CONTACT: ContactValues = {
  name: '',
  type: 'customer',
  cvr: '',
  ean: '',
  email: '',
  address: '',
  city: '',
  zip: '',
  isActive: true,
};

const TYPE_OPTIONS = [
  { value: 'customer', label: 'Kunde' },
  { value: 'supplier', label: 'Leverandør' },
  { value: 'both', label: 'Begge' },
];

// -----------------------------------------------------------------------------
// Creating
// -----------------------------------------------------------------------------

function CreateContactModal({
  opened,
  onClose,
}: {
  readonly opened: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const t = useT();
  const create = useCreateContact();

  const form = useForm<ContactValues>({
    mode: 'uncontrolled',
    initialValues: EMPTY_CONTACT,
    validate: translating(contactSchema, t, 'contacts'),
  });

  function handleSubmit(values: ContactValues): void {
    // Empty means "not recorded", and the API rejects "" against its CVR and
    // EAN patterns — so an empty field is omitted rather than sent blank.
    create.mutate(
      {
        name: values.name.trim(),
        type: values.type,
        ...(values.cvr.trim() === '' ? {} : { cvr: values.cvr.trim() }),
        ...(values.ean.trim() === '' ? {} : { ean: values.ean.trim() }),
        ...(values.email.trim() === '' ? {} : { email: values.email.trim() }),
        ...(values.address.trim() === '' ? {} : { address: values.address.trim() }),
        ...(values.city.trim() === '' ? {} : { city: values.city.trim() }),
        ...(values.zip.trim() === '' ? {} : { zip: values.zip.trim() }),
      },
      {
        onSuccess: (contact) => {
          notifications.show({
            color: 'green',
            title: t('contacts.create.created'),
            message: contact.name,
          });
          form.reset();
          onClose();
        },
      },
    );
  }

  return (
    <Modal opened={opened} onClose={onClose} title={t('contacts.create.title')}>
      {/*
        noValidate: native constraint validation blocks the submit event before
        Mantine sees it, so the Zod schema never runs and the browser shows its
        own message in its own language instead.
      */}
      <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <ContactFields form={form} />

          {create.isError && (
            <ErrorMessage error={create.error} title={t('contacts.create.failed')} />
          )}

          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose}>
              Annullér
            </Button>
            <Button type="submit" loading={create.isPending}>
              Opret
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

// -----------------------------------------------------------------------------
// Editing
// -----------------------------------------------------------------------------

function EditContactModal({
  contact,
  opened,
  onClose,
}: {
  readonly contact: Contact | null;
  readonly opened: boolean;
  readonly onClose: () => void;
}): ReactNode {
  const t = useT();
  const update = useUpdateContact();
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  const form = useForm<ContactValues>({
    mode: 'uncontrolled',
    initialValues: EMPTY_CONTACT,
    validate: translating(contactSchema, t, 'contacts'),
  });

  // Fill from the contact being edited, once per contact, without an effect.
  if (contact !== null && loadedFor !== contact.id) {
    setLoadedFor(contact.id);
    form.setValues({
      name: contact.name,
      type: contact.type,
      cvr: contact.cvr ?? '',
      ean: contact.ean ?? '',
      email: contact.email ?? '',
      address: contact.address ?? '',
      city: contact.city ?? '',
      zip: contact.zip ?? '',
      isActive: contact.isActive,
    });
    form.resetDirty();
  }

  function handleSubmit(values: ContactValues): void {
    if (contact === null) {
      return;
    }

    /*
      Emptying a field has to send `null`, not `''`.

      The API treats an omitted field as "leave it alone" and null as "clear
      it" — two different intentions. An empty string is neither: it would fail
      the eight-digit CVR check rather than removing the CVR. So a field the
      person has cleared is sent as null, and one that has not changed is not
      sent at all.
    */
    const cleared = (current: string, original: string | null): string | null | undefined => {
      const trimmed = current.trim();
      if (trimmed === (original ?? '')) {
        return undefined;
      }
      return trimmed === '' ? null : trimmed;
    };

    const patch: UpdateContact = {
      ...(values.name.trim() === contact.name ? {} : { name: values.name.trim() }),
      ...(values.type === contact.type ? {} : { type: values.type }),
      ...(cleared(values.cvr, contact.cvr) === undefined
        ? {}
        : { cvr: cleared(values.cvr, contact.cvr) }),
      ...(cleared(values.ean, contact.ean) === undefined
        ? {}
        : { ean: cleared(values.ean, contact.ean) }),
      ...(cleared(values.email, contact.email) === undefined
        ? {}
        : { email: cleared(values.email, contact.email) }),
      ...(cleared(values.address, contact.address) === undefined
        ? {}
        : { address: cleared(values.address, contact.address) }),
      ...(cleared(values.city, contact.city) === undefined
        ? {}
        : { city: cleared(values.city, contact.city) }),
      ...(cleared(values.zip, contact.zip) === undefined
        ? {}
        : { zip: cleared(values.zip, contact.zip) }),
      ...(values.isActive === contact.isActive ? {} : { isActive: values.isActive }),
    };

    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }

    update.mutate(
      { id: contact.id, patch },
      {
        onSuccess: (saved) => {
          notifications.show({
            color: 'green',
            title: t('contacts.edit.saved'),
            message: saved.name,
          });
          onClose();
        },
      },
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={contact === null ? '' : t('contacts.edit.title', { name: contact.name })}
    >
      <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <ContactFields form={form} />

          <Switch
            label={t('contacts.active')}
            description={t('contacts.activeHelp')}
            key={form.key('isActive')}
            {...form.getInputProps('isActive', { type: 'checkbox' })}
          />

          {update.isError && (
            <ErrorMessage error={update.error} title={t('contacts.edit.failed')} />
          )}

          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose}>
              Annullér
            </Button>
            <Button type="submit" loading={update.isPending}>
              Gem
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

// -----------------------------------------------------------------------------
// The fields both forms share
// -----------------------------------------------------------------------------

function ContactFields({
  form,
}: {
  readonly form: UseFormReturnType<ContactValues>;
}): ReactNode {
  const t = useT();
  return (
    <>
      <TextInput
        label={t('contacts.col.name')}
        key={form.key('name')}
        {...form.getInputProps('name')}
      />
      <Select
        label={t('contacts.col.type')}
        data={TYPE_OPTIONS}
        allowDeselect={false}
        key={form.key('type')}
        {...form.getInputProps('type')}
      />
      <Group grow align="flex-start">
        <TextInput
          label={t('contacts.cvr')}
          placeholder={t('contacts.cvrPlaceholder')}
          key={form.key('cvr')}
          {...form.getInputProps('cvr')}
        />
        <TextInput
          label={t('contacts.ean')}
          placeholder={t('contacts.eanPlaceholder')}
          key={form.key('ean')}
          {...form.getInputProps('ean')}
        />
      </Group>
      <TextInput
        label={t('contacts.email')}
        key={form.key('email')}
        {...form.getInputProps('email')}
      />

      <Alert color="blue" variant="light" title={t('contacts.addressRequiredTitle')}>
        {t('contacts.addressRequired')}
      </Alert>

      <TextInput
        label={t('contacts.address')}
        key={form.key('address')}
        {...form.getInputProps('address')}
      />
      <Group grow align="flex-start">
        <TextInput
          label={t('contacts.zip')}
          key={form.key('zip')}
          {...form.getInputProps('zip')}
        />
        <TextInput
          label={t('contacts.city')}
          key={form.key('city')}
          {...form.getInputProps('city')}
        />
      </Group>
    </>
  );
}
